import React from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import useStoryblok from '~/utils/storyblok-service';
import { graphql } from 'gatsby';
import Layout from '~/components/Layout';
import Seo from '~/components/SEO';
import TextCard from '~/components/TextCard';
import IconLink from '~/components/IconLink';
import Image from '~/components/Dynamic/Image';
import RichTextRenderer from '~/components/Dynamic/RichTextRenderer';
import FeatureItem from '~/components/FeatureItem';

function ForDevs({ data, location, pageContext }) {
  const story = useStoryblok(data.storyblokEntry, location);
  const content = story.content;

  const {
    Hero,
    apiHeading,
    apiFeatures,
    documentation,
    scaleUpFeatures,
    scaleUpHeading,
    support,
    testingFeatures,
    testingHeading,
    testingTextLeft,
    testingTextRight,
  } = content;

  return (
    <Layout locale={pageContext.locale}>
      <Seo {...content.seo} lang={story.lang} pageContext={pageContext} />
      <section className="pt-10 -mt-20 bg-lime">
        <div className="container flex flex-col items-center py-24 md:py-32 md:flex-row pt-42">
          <div className="flex md:w-6/12">
            <TextCard
              className={`md:max-w-lg sm:pr-10 text-black`}
              subHeadingClass={`text-black text-sh text-sh--solid`}
              subHeading={Hero[0]?.textCard[0]?.subHeading}
              tag={`h1`}
              headingClass={`text-black md:text-h2 lg:text-h1`}
              heading={Hero[0]?.textCard[0]?.heading}
              contentClass={`text-black text-20px`}
              content={Hero[0]?.textCard[0]?.content}
              button={Hero[0]?.textCard[0]?.button}
            />
          </div>
          <div className="flex flex-col mt-16 md:mt-0 md:w-6/12">
            <Image blok={Hero[0]?.Asset[0]} />
          </div>
        </div>
      </section>

      <section className="my-20 md:my-28">
        <div className="container">
          <div className="flex flex-wrap -ml-14 lg:-ml-28">
            <div className="w-full lg:w-5/12 pl-14 lg:pl-28">
              <h2 className="text-h2">{apiHeading}</h2>
            </div>
            <div className="w-full mt-12 lg:w-7/12 pl-14 xl:pl-28 lg:mt-0">
              {apiFeatures.map((autoItem, i) => (
                <div key={`autoItem${i}`} className={`flex -ml-14 ${i !== apiFeatures.length - 1 && 'mb-16'}`}>
                  <div className="w-4/12 md:w-3/12 pl-14">
                    <Image blok={autoItem.icon} />
                  </div>
                  <div className="flex flex-col w-8/12 pl-6 md:w-9/12 md:pl-14">
                    <h4 className="mb-4 text-h4">{autoItem.heading}</h4>
                    <div className="text-17px">
                      <p>{autoItem.content}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="my-20 md:my-28 bg-beige">
        <div className="container flex flex-col items-center text-center py-20 md:py-28">
          <div className="mb-8">
            <h2 className="text-h2">{documentation[0]?.heading}</h2>
          </div>
          <div className="flex flex-wrap prose text-black mb-14 max-w-[34rem] m-auto">
            <p>{documentation[0]?.content}</p>
          </div>
          <div>
            <IconLink to={documentation[0]?.button[0]?.button?.url}>{documentation[0]?.button[0]?.title}</IconLink>
          </div>
        </div>
      </section>

      <section className="my-20 md:my-28">
        <div className="container">
          <div className="mb-8">
            <p className="mb-2 text-sh">{testingHeading[0]?.subheading}</p>
            <h2 className="text-h2">{testingHeading[0]?.heading}</h2>
          </div>
          <div className="flex flex-wrap -ml-24 mb-14 ">
            <div className="w-full pl-24 prose text-black md:w-6/12 ">
              <RichTextRenderer content={testingTextLeft} />
            </div>
            <div className="w-full pl-24 mt-5 prose text-black md:w-6/12 md:mt-0 ">
              <RichTextRenderer content={testingTextRight} />
            </div>
          </div>

          <div className="flex flex-wrap -ml-3">
            {testingFeatures.map((item, i) => (
              <div
                key={`testItem${i}`}
                className={`w-full md:w-6/12 pl-3 ${i !== testingFeatures.length - 1 && 'mb-3'}`}
              >
                <div className="flex items-center px-6 py-4 bg-light-grey">
                  <StaticImage src={`../assets/icons/list-icon.png`} className={`mr-6 w-7 flex-none`} alt="" />
                  <p className="font-semibold text-15px">{item.heading}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-black my-28">
        <div className="container">
          <div className="relative flex items-center justify-center py-20 text-white">
            <div className="flex flex-col items-center max-w-3xl text-center">
              <div className="w-[130px] absolute -top-16 flex-auto">
                <Image blok={support[0]?.icon} />
              </div>
              <h2 className="mb-8 text-h2">{support[0]?.heading}</h2>
              <div className="text-24px max-w-[40rem]">
                <p>{support[0]?.content}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="my-20 md:my-28">
        <div className="container">
          <div className="flex justify-center mb-12 lg:mb-16">
            <TextCard className={`items-center text-center max-w-2xl`} heading={scaleUpHeading} />
          </div>

          <div className="flex flex-wrap justify-around flex-auto -ml-12">
            {scaleUpFeatures.map((devFeat, i) => (
              <div
                key={`scaleUpFeauture${i}`}
                className={`flex focus-within:justify-around w-full pl-12 ${i !== 0 && 'mt-12 lg:mt-0'} lg:w-1/3`}
              >
                <FeatureItem
                  className={`items-center max-w-md flex-auto object-contain `}
                  iconSrc={devFeat.icon.filename}
                  heading={devFeat.heading}
                  content={devFeat.content}
                  contentClass={`text-15px text-center`}
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}

export default ForDevs;

export const query = graphql`
  query ForDevsQuery($id: String!) {
    storyblokEntry(id: { eq: $id }) {
      id
      name
      content
      lang
      slug
      uuid
      full_slug
    }
  }
`;
